import * as React from 'react';
import { useRecordingNavigationGuard } from '../context/RecordingNavigationGuard';
import { styled} from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { FormControl, InputLabel, Select, MenuItem, Avatar, Tooltip, Box, Typography, LinearProgress } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import BrandLogo from './BrandLogo';
// import { ReactComponent as Dashboard} from '../assets/svg/dashboard.svg';
import { ReactComponent as Patient } from '../assets/svg/patient.svg';
import { ReactComponent as Chat} from '../assets/svg/chat.svg';
import { ReactComponent as Scribe} from '../assets/svg/scribe.svg';
import { ReactComponent as CollapseIn} from '../assets/svg/collapse-in.svg';
import { ReactComponent as CollapseOut} from '../assets/svg/collapse-out.svg';
import { ReactComponent as Resource} from '../assets/svg/resource.svg';
import { ReactComponent as Agent} from '../assets/svg/agent.svg';
import { ReactComponent as User} from '../assets/svg/user.svg';
import { ReactComponent as DataExport } from '../assets/svg/export.svg';
import { ReactComponent as Home } from '../assets/svg/home.svg';
import { ReactComponent as Dashboard } from '../assets/svg/home.svg';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import '../App.css';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { setDrawerOpen, setCurrentOrg, setRole } from '../store.js';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.js';
import { useWalletBalance, walletCredits, walletPlanAllotmentCredits } from '../creditWallet/WalletBalanceContext.js';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import TourReplayMenu from './TourReplayMenu';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  boxShadow: `4px 0px 8px rgba(0, 0, 0, 0.2)`,
  backgroundColor: '#F8F9FA',
  padding: '0 5px',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  boxShadow: `4px 0px 8px rgba(0, 0, 0, 0.2)`,
  backgroundColor: '#F8F9FA',
  padding: '0 5px',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});


const DrawerHeader = styled('div')(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'space-between' : 'center',
  padding: open ? "8px 12px" : "8px 4px",
  flexDirection: open ? "row" : "column",
  gap: 4,
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

// Credit balance in the sidebar footer, on the second line of the user block so it
// belongs to the name above it rather than floating as a third row of its own. The
// click target is the whole block; this part is presentation.
//
// A meter needs a denominator and not every wallet has one: the denominator is the
// plan's monthly credit allowance (free 15 / basic 30 / pro 100), derived from the
// subscription tier — the wallet row's allotment_halves is the REMAINING allotment
// this cycle, so using it as the denominator would show "7.5 of 7.5" after spending
// half of a 15-credit allowance. Wallets with no known tier keep the plain caption —
// a bar measuring nothing would read as permanently empty. Balance can also exceed
// the allowance after a top-up, so the fill is clamped rather than overflowing its
// track.
//
// The bar shows credits REMAINING. It is the same shape people know from meters that
// fill up as they achieve something, and this one drains, so the wording beside it has
// to stay "how much is left" and never "how much you have used".
function SidebarCreditsLine() {
  const { wallet, loading, planTier } = useWalletBalance();
  const credits = walletCredits(wallet);
  const allotment = walletPlanAllotmentCredits(planTier);
  if (loading && credits === null) return null;

  const outOfCredits = credits !== null && credits <= 0;
  const color = outOfCredits ? '#B91C1C' : '#0F766E';
  const percentLeft = credits !== null && allotment !== null
    ? Math.min(100, Math.max(0, (credits / allotment) * 100))
    : null;
  const resetsOn = allotment !== null && wallet?.allotment_expires_at
    ? new Date(wallet.allotment_expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null;

  // `rest` is everything after the bolded balance, so the two never drift apart.
  const rest = allotment !== null ? ` of ${allotment} credits left` : ' credits available';
  const label = credits === null ? 'Credits unavailable' : `${credits}${rest}`;

  return (
    <Box role="status" aria-label={label} sx={{ color, mt: 0.25 }}>
      {percentLeft !== null && (
        <LinearProgress
          variant="determinate"
          value={percentLeft}
          sx={{
            height: 3,
            borderRadius: 2,
            mb: 0.375,
            backgroundColor: '#E5E7EB',
            '& .MuiLinearProgress-bar': { backgroundColor: outOfCredits ? '#DC2626' : '#29C0BB', borderRadius: 2 },
          }}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {percentLeft === null && <TollOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />}
        <Typography noWrap sx={{ fontSize: 11, lineHeight: 1.3, color: 'inherit' }}>
          {credits === null ? label : (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>{credits}</Box>{rest}
              {resetsOn && <Box component="span" sx={{ color: '#9CA3AF' }}> · resets {resetsOn}</Box>}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

const CustomListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'currentTab' && prop !== 'text',
})(({ theme, currentTab, text }) => ({
  '&:hover': {
    backgroundColor: currentTab === text ? '#29C0BB' : theme.palette.action.hover
  },
}));


export default function MiniDrawer() {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.globalState.open, shallowEqual);
  const currentTab = useSelector((state) => state.globalState.currentTab, shallowEqual);
  const navigate = useNavigate();
  const { confirmNavigation } = useRecordingNavigationGuard();

  const role = useSelector((state) => state.globalState.role, shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const allOrgs = useSelector((state) => state.globalState.allOrgs, shallowEqual);
  const { userDetails } = useSelector((state) => ({ userDetails: state.globalState.userDetails }), shallowEqual);
  // The profile photo is not in redux (only name/email are); read it for the footer.
  const [profilePictureUrl, setProfilePictureUrl] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    if (!userDetails?.id) { setProfilePictureUrl(''); return undefined; }
    supabase.from('user_accounts').select('profile_picture_url').eq('id', userDetails.id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setProfilePictureUrl(data?.profile_picture_url || ''); })
      .catch(() => { if (!cancelled) setProfilePictureUrl(''); });
    return () => { cancelled = true; };
  }, [userDetails?.id]);
  const userInitials = (() => {
    const source = (userDetails?.name || userDetails?.email || '').trim();
    if (!source) return '';
    const words = source.split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : source.slice(0, 2)).toUpperCase();
  })();

  // In-development tabs are enabled per environment through REACT_APP_BETA_TABS,
  // a comma list of: chats, agents, resources (all on in .env.dev, empty on
  // uat/prod). Adjust the env files, never the code, to show/hide one.
  const betaTabs = (process.env.REACT_APP_BETA_TABS || '').split(',').map((s) => s.trim()).filter(Boolean);
  const sideMenuOptions = role === 'Guest'
    ? ['Data Export']
    : ['Dashboard', 'Appointments', ...(betaTabs.includes('chats') ? ['Chats'] : []), 'Client', 'Billing', ...(betaTabs.includes('resources') ? ['Resource Library'] : []), ...(betaTabs.includes('agents') ? ['AI Agent'] : []), 'Data Export'];

  const changeTab = (tab) => {
    // General navigation
    if (tab === 'Chats') {
      navigate('/chats');
    } else if (tab === 'Client') {
      navigate('/consultation');
    } else if (tab === 'Appointments') {
      navigate('/appointments');
    } else if(tab === 'Resource Library'){
      navigate('/resources');
    } else if(tab === 'AI Agent'){
      navigate('/agents');
    } else if(tab === 'Data Export') {
      navigate('/export');
    } else if(tab === 'User Management') {
      navigate('/users');
    } else if(tab === 'Dashboard') {
      navigate('/dashboard');
    } else if(tab === 'Billing') {
      navigate('/billings');
    }
  }

  const getTeamID = async (teamName) => {
    const { data, error } = await supabase.from('teams').select('*');
    if (error) {
      console.error('Error getting team ID: ', error.message);
      return null;
    } else {
      const decryptedData = await Promise.all(data.map(async (team) => {
        const decryptedTeamName = await supabase.rpc('decrypt_text', { encrypted_data: team.name });
        if (decryptedTeamName.error) {
          console.error("Error: ", decryptedTeamName.error);
          return null;
        }
        return { ...team, name: decryptedTeamName.data };
      }));
      return decryptedData.find(team => team.name === teamName)?.id;
    }
  };

  const handleOrgChange = async (event) => {
    if (!confirmNavigation()) return;
    const teamID = await getTeamID(event.target.value);
    const { data, error } = await supabase.from('team_users').select('role').eq('user_id', userDetails?.id).eq('team_id', teamID);
    if (error) {
      console.error('Error getting role: ', error.message);
    }
    if (!confirmNavigation()) return;
    dispatch(setRole(data[0].role));
    dispatch(setCurrentOrg({ id: teamID, name: event.target.value }));
    navigate('/consultation');
    window.location.reload();
  };

  return (
      <Drawer variant="permanent" open={open}>
        <DrawerHeader open={open}>
          <BrandLogo compact={!open} width={open ? 160 : 32} height={open ? 40 : 32} />
          <IconButton aria-label={open ? "Collapse sidebar" : "Expand sidebar"} onClick={() => {dispatch(setDrawerOpen(open === true ? false : true)); }}>
            {open ? <CollapseIn style={{width: 28, height: 28}}/> : <CollapseOut style={{width: 28, height: 28}}/>}
          </IconButton>
        </DrawerHeader>

        {open && allOrgs && currentOrg && (
          <FormControl variant="outlined" size="small" sx={{ mx: 2, my: 1 }}>
            <InputLabel id="drawer-org-label">Organization</InputLabel>
            <Select
              labelId="drawer-org-label"
              label="Organization"
              value={currentOrg.name}
              onChange={handleOrgChange}
              sx={{ backgroundColor: '#ffffff', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' } }}
            >
              {allOrgs.map((v, index) => (
                <MenuItem key={index} value={v.name}>{v.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!open && currentOrg && (
          <Tooltip title={currentOrg.name} placement="right">
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <Avatar sx={{ width: 36, height: 36, backgroundColor: '#29C0BB', fontSize: 13, fontWeight: 700, cursor: 'default' }}>
                {currentOrg.name.includes(' ')
                  ? currentOrg.name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('')
                  : currentOrg.name.slice(0, 2)}
              </Avatar>
            </Box>
          </Tooltip>
        )}

        <List>
          {sideMenuOptions.map((text, index) => (
            <ListItem key={text} disablePadding sx={{ display: 'block' }}>
              <CustomListItemButton
                data-tour={text === 'Client' ? 'nav-patient' : text === 'Appointments' ? 'nav-appointments' : undefined}
                currentTab={currentTab}
                text={text}
                onClick={() => { changeTab(text); }}
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                    borderRadius: '16px',
                    backgroundColor: currentTab === text ? '#29C0BB' : 'inherit',
                  },
                  open
                    ? {
                        justifyContent: 'initial',
                      }
                    : {
                        justifyContent: 'center',
                      },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      flex: '0 0 40px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'white',
                      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      padding: '10px',
                      '& > svg': {
                        width: 20,
                        height: 20,
                        display: 'block',
                      },
                    },
                    open
                      ? {
                          mr: 3,
                        }
                      : {
                          mr: 'auto',
                        },
                  ]}
                >
                  {
                    /* Map icons by tab name, not index — the option list
                       changes with role and showBetaTabs, so indexes shift. */
                    text === 'Dashboard' ? <Dashboard /> :
                    text === 'Appointments' ? <CalendarMonthIcon style={{ color: '#29C0BB' }} /> :
                    text === 'Chats' ? <Chat /> :
                    text === 'Client' ? <Patient /> :
                    text === 'Billing' ? <AttachMoneyIcon style={{ color: '#29C0BB' }} /> :
                    text === 'Resource Library' ? <Resource /> :
                    text === 'AI Agent' ? <Agent /> :
                    text === 'Data Export' ? <DataExport /> :
                    <User />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  sx={[
                    open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                        },
                  ]}
                />
              </CustomListItemButton>
            </ListItem>
          ))}

          {/* Settings — always shown for all roles */}
          <ListItem key="Settings" disablePadding sx={{ display: 'block' }}>
            <CustomListItemButton
              data-tour="nav-settings"
              currentTab={currentTab}
              text="Settings"
              onClick={() => navigate('/settings')}
              sx={[
                { minHeight: 48, px: 2.5, borderRadius: '16px', backgroundColor: currentTab === 'Settings' ? '#29C0BB' : 'inherit' },
                open ? { justifyContent: 'initial' } : { justifyContent: 'center' },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    flex: '0 0 40px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    padding: '10px',
                    '& > svg': { width: 20, height: 20, display: 'block' },
                  },
                  open ? { mr: 3 } : { mr: 'auto' },
                ]}
              >
                <SettingsIcon style={{ color: '#29C0BB' }} />
              </ListItemIcon>
              <ListItemText primary="Settings" sx={[open ? { opacity: 1 } : { opacity: 0 }]} />
            </CustomListItemButton>
          </ListItem>
        </List>
        {/* Footer: who is signed in, their credit balance, and the tours "?" — one quiet
            card in the same white/teal language as the nav tiles. Clicking the card opens
            Settings (profile + subscription). */}
        {userDetails && (
          <Box sx={{ mt: 'auto', px: open ? 1 : 0.5, pb: 1.5, pt: 1 }}>
            <Box
              data-tour="nav-current-user"
              sx={{
                borderTop: '1px solid #E5E7EB',
                pt: 1.5,
                px: open ? 0.75 : 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: open ? 'stretch' : 'center',
                gap: open ? 0.75 : 1,
              }}
            >
              {open ? (
                // Name over balance in one column beside the avatar. The email moved to
                // the avatar's tooltip: truncated to "shaokang.cai@tikc…" it told the
                // signed-in user nothing they did not know, and it was occupying the
                // line the balance reads far better on.
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Tooltip title={userDetails.name && userDetails.email ? userDetails.email : ''} placement="right">
                    <Avatar
                      src={profilePictureUrl || undefined}
                      onClick={() => navigate('/settings')}
                      sx={{ width: 40, height: 40, backgroundColor: '#29C0BB', fontSize: 13, fontWeight: 700, flexShrink: 0, cursor: 'pointer' }}
                    >
                      {userInitials}
                    </Avatar>
                  </Tooltip>
                  <Box sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }} onClick={() => navigate('/settings')}>
                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>
                      {userDetails.name || userDetails.email || 'Signed in'}
                    </Typography>
                    <SidebarCreditsLine />
                  </Box>
                  {role !== 'Guest' && <TourReplayMenu inline />}
                </Box>
              ) : (
                <>
                  <Tooltip title={userDetails.name || userDetails.email || ''} placement="right">
                    <Avatar
                      src={profilePictureUrl || undefined}
                      onClick={() => navigate('/settings')}
                      sx={{ width: 36, height: 36, backgroundColor: '#29C0BB', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      {userInitials}
                    </Avatar>
                  </Tooltip>
                  {role !== 'Guest' && <TourReplayMenu inline />}
                </>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
  );
}
